import React from "react";
import { Space } from "antd";
import { MdDelete, MdModeEditOutline, MdEdit } from "react-icons/md";
import { EllipsisOutlined } from "@ant-design/icons";
import { Button, Col, Popover, Row } from "antd";
import TNButton from "../../../../../reusable-components/TNButton";
import {
  ButtonType,
  ButtonShape,
  ButtonSize,
} from "../../../../../enums/ButtonAttributesEnums";
import ConditionalRender from "../../../../../reusable-components/ConditionalRender";
import { MaterialTruck } from "../../../../../context/materialListingContext";
import styles from "./MaterialDetails.module.scss";
import * as Yup from "yup";
import { formatNumber } from "../../../../../utils/numberFormatterUtil";
import clsx from "clsx";

export const CONSTANTS_DATA = {
  NO_DATA: "No materials have been added yet",
  TITLE: "Add Material Details",
};

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
  isEditable?: boolean;
  handleDeleteRow?: (row?: any) => void;
  handleEditRow?: (row?: any) => void;
}

export const getColumns: ColumnsType<DataType> = ({
  handleSelectVendor,
  isEditable,
  handleDeleteRow,
  handleEditRow,
  handleOpenBidTool,
}: ColumnsProps) => {
  return [
    {
      title: "Vendor",
      dataIndex: "vendorName",
      key: "vendorName",
      width: "220px",
      render: (text, record) => (
        <span
          className={clsx({
            [styles.vendorHighlight]: true,
            [styles.blockedOrder]: record?.isBlocked,
          })}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: "220px",
      render: (text, record) => (
        <span
          className={clsx({
            [styles.blockedOrder]: record?.isBlocked,
          })}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Truck Type",
      dataIndex: "truckType",
      key: "truckType",
      width: "128px",
      render: (text, record) => (
        <span
          className={clsx({
            [styles.blockedOrder]: record?.isBlocked,
          })}
        >
          {text && text.join ? text.join(" , ") : text || "-"}
        </span>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "materialQuantity",
      key: "materialQuantity",
      width: "105px",
      render: (text, record) => (
        <ConditionalRender
          visible={text !== undefined || text !== null}
          fallback={"-"}
        >
          <span
            className={clsx({
              [styles.blockedOrder]: record?.isBlocked,
            })}
          >
            {formatNumber(Number(text).toFixed(2)) +
              " " +
              record?.materialQuantityUnitType}
          </span>
        </ConditionalRender>
      ),
    },
    {
      title: "Material Cost",
      dataIndex: "materialCost",
      key: "materialCost",
      width: "128px",
      render: (text, record) => (
        <ConditionalRender
          visible={text !== undefined || text !== null}
          fallback={"-"}
        >
          <span
            className={clsx({
              [styles.blockedOrder]: record?.isBlocked,
            })}
          >
            {"$" +
              formatNumber(Number(text).toFixed(2)) +
              " / " +
              record?.materialCostType}
          </span>
        </ConditionalRender>
      ),
    },
    {
      title: "Material Price",
      dataIndex: "materialPrice",
      key: "materialPrice",
      width: "132px",
      render: (text, record) => (
        <ConditionalRender
          visible={text !== undefined || text !== null}
          fallback={"-"}
        >
          <span
            className={clsx({
              [styles.blockedOrder]: record?.isBlocked,
            })}
          >
            {"$" +
              formatNumber(Number(text).toFixed(2)) +
              " / " +
              record?.materialPriceType}
          </span>
        </ConditionalRender>
      ),
    },
    {
      title: "Trucking Cost",
      dataIndex: "truckingCost",
      key: "truckingCost",
      width: "132px",
      render: (text, record) => (
        <ConditionalRender
          visible={text !== undefined || text !== null}
          fallback={"-"}
        >
          <span
            className={clsx({
              [styles.blockedOrder]: record?.isBlocked,
            })}
          >
            {"$" +
              formatNumber(Number(text).toFixed(2)) +
              " / " +
              record?.truckingCostType}
          </span>
        </ConditionalRender>
      ),
    },
    {
      title: "Trucking Price",
      dataIndex: "truckingPrice",
      key: "truckingPrice",
      width: "136px",
      render: (text, record) => (
        <ConditionalRender
          visible={text !== undefined || text !== null}
          fallback={"-"}
        >
          <span
            className={clsx({
              [styles.blockedOrder]: record?.isBlocked,
              [styles.truckingPriceHighlight]: true,
            })}
            style={{
              cursor: "pointer",
            }}
            onClick={() => {
              handleOpenBidTool(record);
            }}
          >
            {"$" +
              formatNumber(Number(text).toFixed(2)) +
              " / " +
              record?.truckingPriceType}
          </span>
        </ConditionalRender>
      ),
    },
    {
      title: "Landed Price",
      dataIndex: "landedPrice",
      key: "landedPrice",
      width: "128px",
      render: (text, record) => {
        return (
          <ConditionalRender
            visible={record?.truckingPriceType === record?.materialPriceType}
            fallback={" - "}
          >
            <span
              className={clsx({
                [styles.blockedOrder]: record?.isBlocked,
              })}
            >
              {`$${formatNumber(Number(text).toFixed(2))} / ${
                record?.landedPriceType
              }`}
            </span>
          </ConditionalRender>
        );
      },
    },
    ...(isEditable
      ? [
          {
            title: "",
            dataIndex: "actions",
            key: "actions",
            sorter: false,
            width: "3%",
            fixed: "right",
            render: (actions, record) => {
              return (
                <Row
                  style={{
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    flexWrap: "nowrap",
                  }}
                >
                  <Col>
                    <Popover
                      placement="bottomRight"
                      content={
                        <>
                          <Row
                            className="popovergrid"
                            style={{ maxWidth: "90px" }}
                          >
                            {
                              <Col span={24}>
                                <Button
                                  className="btn_edit"
                                  onClick={() => handleDeleteRow(record)}
                                >
                                  <span className="span_edit">
                                    <MdDelete
                                      className="table-font-mobile"
                                      style={{ marginRight: "5px" }}
                                    />{" "}
                                    Delete
                                  </span>
                                </Button>
                              </Col>
                            }
                          </Row>
                          <ConditionalRender visible={!record?.isBlocked}>
                            <Row
                              className="popovergrid"
                              style={{ maxWidth: "90px" }}
                            >
                              {
                                <Col span={24}>
                                  <Button
                                    className="btn_edit"
                                    onClick={() => handleEditRow(record)}
                                  >
                                    <span className={styles.span_edit}>
                                      <MdEdit className="table-font-mobile" />{" "}
                                      Edit
                                    </span>
                                  </Button>
                                </Col>
                              }
                            </Row>
                          </ConditionalRender>
                        </>
                      }
                    >
                      <EllipsisOutlined
                        style={{ fontSize: "30px", color: "#C3C8CD" }}
                        className="action-ellipsis-button"
                      />
                    </Popover>
                  </Col>
                </Row>
              );
            },
          },
        ]
      : []),
  ];
};

export const getEditMaterialDefaultValues = (row) => {
  return {
    vendorName: row?.vendorName,
    materialName: row?.materialName,
    truckType: row?.truckType,
    quantity: formatNumber(Number(row?.materialQuantity)?.toFixed(2)),
    quantityUnitType: row?.materialQuantityUnitType,
    materialCost: formatNumber(Number(row?.materialCost)?.toFixed(2)),
    materialCostType: row?.materialCostType,
    materialPrice: formatNumber(Number(row?.materialPrice)?.toFixed(2)),
    materialPriceType: row?.materialPriceType,
    truckingCost: formatNumber(Number(row?.truckingCost)?.toFixed(2)),
    truckingCostType: row?.truckingCostType,
    truckingPrice: formatNumber(Number(row?.truckingPrice)?.toFixed(2)),
    truckingPriceType: row?.truckingPriceType,
    landedPrice: formatNumber(Number(row?.landedPrice)?.toFixed(2)),
    landedPriceType: row?.landedPriceType,
    minimumHours: row?.minimumHours
      ? Number(row?.minimumHours)?.toFixed(2)
      : undefined,
    id: row?.id,
  };
};
const cleanNumber = (value: any, originalValue: any) => {
  if (typeof originalValue === "string") {
    const cleaned = originalValue.replace(/,/g, "").trim();
    if (cleaned === "") return undefined;
    const parsed = Number(cleaned);
    return isNaN(parsed) ? undefined : parsed;
  }
  return value;
};

export const editMaterialValidationSchema = Yup.object({
  materialCost: Yup.number()
    .nullable()
    .transform(cleanNumber)
    .typeError("Invalid entry")
    .test("is-required", "required", (v) => v !== undefined && v !== null)
    .min(0, "Material cost cannot be negative"),
  materialPrice: Yup.number()
    .nullable()
    .transform(cleanNumber)
    .typeError("Invalid entry")
    .test("is-required", "required", (v) => v !== undefined && v !== null)
    .min(0, "Material price cannot be negative"),
  truckingPrice: Yup.number()
    .nullable()
    .transform(cleanNumber)
    .typeError("Invalid entry")
    .test("is-required", "required", (v) => v !== undefined && v !== null)
    .min(0, "Trucking price cannot be negative"),
  truckingCost: Yup.number()
    .nullable()
    .transform(cleanNumber)
    .typeError("Invalid entry")
    .test("is-required", "required", (v) => v !== undefined && v !== null)
    .min(0, "Trucking cost cannot be negative"),
  minimumHours: Yup.number()
    .nullable()
    .transform(cleanNumber)
    .typeError("Invalid entry")
    .min(0, "Minimum Hours cannot be negative"),
});
